import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "../api/client";
import { RUN_IN_FLIGHT_LINE, WRITE_CONFLICT_LINE } from "../lib/runNotify";

// 0028: PATCH /api/v1/products/{pid}/title with optimistic-cache
// invalidation. On success, refetches both the product tree and the
// products list so dashboard + breadcrumbs reflect the new title.
// Translates the server's product.title_locked code into a friendlier
// founder-facing message (in practice the UI hides the edit
// affordance until source ≠ placeholder, so this is defensive).
// 0159 F1: product.run_in_flight (a rename while an analysis is running; the
// pencil is hidden then, so this is a stale tab's refusal) and
// product.write_conflict (the commit race) read the two ruled lines from
// lib/runNotify. The thrown ProductTitleError also carries the code, which
// the shared in-flight refusal rule reads (hooks/useRunInFlightRefusal).

// ProductTitleError is a refused rename: the founder's line as its message
// (EditableTitle renders it inline) and the error envelope's code.
export class ProductTitleError extends Error {
  readonly code: string | undefined;
  constructor(message: string, code: string | undefined) {
    super(message);
    this.code = code;
  }
}

export function usePatchProductTitle(productId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (title: string) => {
      const { error } = await apiClient.PATCH("/api/v1/products/{pid}/title", {
        params: {
          path: { pid: productId },
          header: { "X-Requested-With": "xhr" },
        },
        body: { title },
      });
      if (error) {
        const env = error as { error?: { code?: string; message?: string } };
        const code = env.error?.code;
        const message = env.error?.message ?? "Failed to update title";
        throw new ProductTitleError(
          code === "product.title_locked"
            ? "Title can't be edited until the first analysis completes."
            : code === "product.run_in_flight"
              ? RUN_IN_FLIGHT_LINE
              : code === "product.write_conflict"
                ? WRITE_CONFLICT_LINE
                : message,
          code,
        );
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["product", productId, "tree"] });
      qc.invalidateQueries({ queryKey: ["products"] });
    },
  });
}
